import React, { useState, useEffect, useRef } from 'react';
import { User, FolderOpen, Palette, Heart, Phone, HelpCircle, ChevronUp } from 'lucide-react';

const ProjectsPage = () => {
  const projects = [
    {
      id: 'expenseManager',
      name: 'Finly - Whisper Wealth',
      timeline: 'Apr 2025 - May 2025',
      technologies: ['Frappe', 'Python', 'JavaScript', 'MariaDB', 'Vue', 'Telegram APIs', 'Deepgram API', 'Gemini API'],
      description: [
        'Built a multilingual expense tracker supporting 36 languages, with automatic budget classification and visual analytics using Frappe Framework and MariaDB.',
        'Integrated a Telegram bot with the Frappe backend, enabling users to log categorized expenses from voice notes via Deepgram and Gemini APIs; validated with 50+ voice logs.',
      ],
      image: 'images/Finly.png',
    },
    {
      id: 'parsecLive',
      name: 'ParsecLive',
      timeline: 'Apr 2024',
      technologies: ['React.js', 'Node.js', 'Express', 'JWT', 'Multer', 'MongoDB', 'Git'],
      description: [
        'Developed an event ticketing platform with real-time event management, evaluated by 15–20 users during its initial launch.',
        'Integrated secure user authentication using JWT and built an admin panel for file uploads via Multer.',
        'Managed cloud infrastructure with MongoDB Atlas and utilized GitHub access.',
      ],
      image: 'images/ParsecLive.png',
    },
    {
      id: "conversAI",
      name: "ConversAI: Chat with Multiple PDFs",
      timeline: "June 2024 - July 2024",
      technologies: ["Python", "Streamlit", "PyPDF2", "FAISS", "LangChain", "Google Generative AI"],
      description: [
        "Developed an intelligent chatbot application that allows users to upload and interact with multiple PDF documents through natural language queries.",
        "Integrated LangChain and FAISS for efficient document chunking, embedding, and retrieval, enabling multi-document support and contextual Q&A.",
        "Used Streamlit for a lightweight and interactive frontend and integrated Google Generative AI to generate accurate, conversational responses.",
        "Facilitated intuitive navigation of complex documents such as research papers, contracts, and reports through secure local processing."
      ],
      image: "images/ConverseAI.png"
    },
    {
      id: "archivist",
      name: "Archivist",
      timeline: "May 2024 - July 2024",
      technologies: ["Flask", "Python", "Peewee ORM", "PostgreSQL", "HTML", "CSS", "JavaScript", "bcrypt", "WeasyPrint"],
      description: [
        "Built a comprehensive library management system using Flask and Peewee ORM for managing books, members, transactions, and reports.",
        "Implemented secure user authentication with bcrypt, along with membership management, book issue/return, and receipt generation.",
        "Designed user-friendly interfaces for Browse and filtering books, applying for membership, and managing transactions.",
        "Integrated PostgreSQL for structured data handling and WeasyPrint for generating downloadable PDF receipts and reports."
      ],
      image: "images/Archivist.png"
    }
  ];

  const navItems = [
    { icon: User, label: 'Me', active: false },
    { icon: FolderOpen, label: 'Projects', active: true },
    { icon: Palette, label: 'Skills', active: false },
    { icon: Heart, label: 'Fun', active: false },
    { icon: Phone, label: 'Contact', active: false },
    { icon: HelpCircle, label: '', active: false }
  ];

  const [messages, setMessages] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const generateProjectResponse = (userPrompt, clickedProjectId = null) => {
    let responseContent = "";
    let project = null;

    if (clickedProjectId) {
      project = projects.find(p => p.id === clickedProjectId);
    } else if (userPrompt.toLowerCase().includes("show me your projects") || userPrompt.toLowerCase().includes("projects")) {
      responseContent = {
        type: 'project-grid',
        content: projects.map(project => ({
          id: project.id,
          name: project.name,
          image: project.image
        }))
      };
      return responseContent;
    } else {
      project = projects.find(p =>
        userPrompt.toLowerCase().includes(p.name.toLowerCase()) ||
        userPrompt.toLowerCase().includes(p.id.toLowerCase())
      );
    }

    if (project) {
      responseContent = `### **${project.name}**\n\n**Timeline:** ${project.timeline}\n\n**Technologies:** ${project.technologies.map(tech => `\`${tech}\``).join(' ')}\n\n**Description:**\n${project.description.map(desc => `- ${desc}`).join('\n')}`;
    } else {
      responseContent = "I can show you my projects! You can ask me to 'Show me your projects' or click on a specific project image once they appear.";
    }
    return responseContent;
  };

  const sendMessage = (promptText, projectData = null) => {
    setMessages((prevMessages) => [...prevMessages, { type: 'user', content: promptText }]);
    setCurrentInput('');
    setIsTyping(true);

    setTimeout(() => {
      let aiResponse;
      if (projectData && projectData.id) {
        aiResponse = generateProjectResponse(`Tell me about ${projectData.name}`, projectData.id);
      } else {
        aiResponse = generateProjectResponse(promptText);
      }
      setMessages((prevMessages) => [...prevMessages, { type: 'ai', content: aiResponse }]);
      setIsTyping(false);
    }, 2000);
  };

  const handleProjectClick = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    if (project) {
      sendMessage(`Tell me about ${project.name}`, project);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const autoPromptText = "Show me your projects.";

    let charIndex = 0;
    const typingInterval = setInterval(() => {
      if (charIndex <= autoPromptText.length) {
        setCurrentInput(autoPromptText.substring(0, charIndex));
        charIndex++;
      } else {
        clearInterval(typingInterval);
        sendMessage(autoPromptText);
      }
    }, 50);

    return () => {
      clearInterval(typingInterval);
    };
  }, []);

  const renderContent = (messageContent) => {
    if (typeof messageContent === 'object' && messageContent.type === 'project-grid') {
      return (
        <div className="grid grid-cols-2 gap-4 mt-4">
          {messageContent.content.map(project => (
            <div
              key={project.id}
              className="cursor-pointer bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
              onClick={() => handleProjectClick(project.id)}
            >
              <img
                src={project.image}
                alt={project.name}
                className="w-full h-32 sm:h-40 object-cover rounded-t-lg"
              />
              <p className="text-center p-2 text-sm sm:text-base font-semibold text-gray-800">
                {project.name}
              </p>
            </div>
          ))}
        </div>
      );
    }

    return messageContent.split('\n').map((line, lineIndex) => {
      if (line.startsWith('## ')) {
        return <h2 key={lineIndex} className="text-xl sm:text-2xl md:text-3xl font-bold mt-2 sm:mt-3 mb-2 sm:mb-3">{line.substring(3)}</h2>;
      }
      if (line.startsWith('### ')) {
        const match = line.match(/### \*\*(.*?)\*\*/);
        if (match) {
          return (
            <h3 key={lineIndex} className="text-lg sm:text-xl font-bold mt-2 sm:mt-3 mb-1 sm:mb-2 text-blue-800">
              {match[1]}
            </h3>
          );
        }
        return <h3 key={lineIndex} className="text-lg sm:text-xl font-semibold mt-2 sm:mt-3 mb-1 sm:mb-2">{line.substring(4)}</h3>;
      }

      let formattedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      formattedLine = formattedLine.replace(/\*(.*?)\*/g, '<em>$1</em>');

      formattedLine = formattedLine.replace(/`(.*?)`/g, '<span class="px-1.5 py-0.5 sm:px-2 sm:py-1 bg-gray-700 text-white text-xs sm:text-sm font-medium rounded-md inline-block mr-0.5 my-0.5 sm:mr-1 sm:my-1">$1</span>');

      if (formattedLine.startsWith('- ')) {
        return <li key={lineIndex} dangerouslySetInnerHTML={{ __html: formattedLine.substring(2) }} className="ml-5 list-disc text-sm sm:text-base leading-normal sm:leading-relaxed text-justify"></li>;
      }
      return <p key={lineIndex} dangerouslySetInnerHTML={{ __html: formattedLine }} className="leading-normal sm:leading-relaxed text-sm sm:text-base"></p>;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-800 flex flex-col text-sm sm:text-base">
      <div className="flex-grow mx-4 sm:mx-8 md:mx-12 lg:mx-[10%] py-6 sm:py-8 md:py-10 lg:py-12 overflow-y-auto">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8 sm:mb-10 md:mb-12 space-y-3 sm:space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} gap-2 sm:gap-3`}
              >
                {message.type === 'ai' && (
                  <img
                    src="images/Siddharth_Singh_Khati.png"
                    alt="AI Profile"
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover self-start mb-1"
                  />
                )}
                <div
                  className={`max-w-[80%] sm:max-w-[70%] py-1.5 px-2.5 sm:py-2 sm:px-3 rounded-2xl sm:rounded-3xl text-sm sm:text-base ${
                    message.type === 'user'
                      ? 'bg-blue-900 text-white'
                      : 'text-gray-800'
                  }`}
                >
                  {renderContent(message.content)}
                </div>
                {message.type === 'user' && (
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-600 flex items-center justify-center text-white self-end mb-1">
                    You
                  </div>
                )}
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start gap-2 sm:gap-3">
                <img
                  src="images/Siddharth_Singh_Khati.png"
                  alt="AI Profile"
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover self-start mb-1"
                />
                <div className="max-w-[70%] p-3 sm:p-4 rounded-xl bg-white border border-gray-200">
                  <div className="flex items-center space-x-1 sm:space-x-1.5">
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-500 rounded-full animate-bounce delay-100"></span>
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-500 rounded-full animate-bounce delay-200"></span>
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-500 rounded-full animate-bounce delay-300"></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 left-0 right-0 bg-gray-50 pt-3 sm:pt-4 border-t border-gray-100">
        <div className="mx-4 sm:mx-8 md:mx-12 lg:mx-[10%] pb-6 sm:pb-8">
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Ask me anything"
                className="w-full px-3.5 py-2.5 sm:px-4 sm:py-3 bg-gray-100 border border-gray-200 rounded-full text-sm sm:text-base text-gray-700 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={currentInput}
                readOnly
              />
              <button className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-600 text-white p-1.5 sm:p-2 rounded-full hover:bg-blue-700 transition-colors">
                <ChevronUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectsPage;